import { Fragment } from "react";
import type { LocationDeal } from "~/graphql-client/generated";
import { Box } from "../Box/Box";
import { Button } from "../Button/Button";
import { ModalDrawer } from "../ModalDrawer/ModalDrawer";
import { Stack } from "../Stack/Stack";
import { Text } from "../Text/Text";

const TITLE = "Exclusive deal limit";

// Renders deal titles inline in bold, joined grammatically (e.g. "A", "A and B", "A, B and C")
function renderDealNames(deals: LocationDeal[]): React.ReactNode {
  return deals.map((deal, index) => (
    <Fragment key={deal.id}>
      {index > 0 && (index === deals.length - 1 ? " and " : ", ")}
      <Text inline weight="strong">
        {deal.title}
      </Text>
    </Fragment>
  ));
}

// Explains the conflict, tailored to which deal (new or existing) is exclusive
function ConflictMessage(props: {
  newDeal: LocationDeal | null;
  dealsToRemove: LocationDeal[];
}): React.ReactNode {
  const { newDeal, dealsToRemove } = props;

  // Fallback message, shouldn't normally happen
  if (!newDeal || dealsToRemove.length === 0) {
    return (
      <Text>
        Exclusive deals can't be combined with other deals. Choose which deal
        you'd like to use.
      </Text>
    );
  }

  const dealsToRemoveNames = renderDealNames(dealsToRemove);

  if (newDeal.exclusive) {
    return (
      <Text>
        <Text inline weight="strong">
          {newDeal.title}
        </Text>{" "}
        is an exclusive deal and can't be combined with other deals. Adding it
        will remove {dealsToRemoveNames} from your cart.
      </Text>
    );
  }

  const isMultiple = dealsToRemove.length > 1;

  return (
    <Text>
      {dealsToRemoveNames}{" "}
      {isMultiple ? "are exclusive deals" : "is an exclusive deal"} and can't be
      combined with other deals. Adding{" "}
      <Text inline weight="strong">
        {newDeal.title}
      </Text>{" "}
      will remove {isMultiple ? "them" : "it"} from your cart.
    </Text>
  );
}

interface Props {
  open: boolean;
  newDeal: LocationDeal | null;
  dealsToRemove: LocationDeal[];
  onReplace: () => void;
  onKeepCurrent: () => void;
}

function ConflictActions(props: {
  newDeal: LocationDeal | null;
  onReplace: () => void;
  onKeepCurrent: () => void;
}): React.ReactNode {
  const replaceLabel = props.newDeal
    ? `Remove & add ${props.newDeal.title}`
    : "Remove & continue";

  return (
    <Stack space="2">
      <Button primary onClick={props.onReplace}>
        {replaceLabel}
      </Button>
      {/* Grey fill behind the transparent secondary button so it stays distinct
          from the beige surface while keeping its hover/active states. */}
      <Box background="neutral-300" borderRadius="standard">
        <Button onClick={props.onKeepCurrent}>Keep my current cart</Button>
      </Box>
    </Stack>
  );
}

// Confirmation shown when adding a deal would break the rule that
// an exclusive deal must be the only deal in the cart.
export function ExclusiveDealConflictDialog(props: Props): React.ReactNode {
  const { open, newDeal, dealsToRemove, onKeepCurrent, onReplace } = props;

  return (
    <ModalDrawer
      open={open}
      title={TITLE}
      onClose={onKeepCurrent}
      background="jets-beige"
      footer={
        <ConflictActions
          newDeal={newDeal}
          onReplace={onReplace}
          onKeepCurrent={onKeepCurrent}
        />
      }
    >
      <ConflictMessage newDeal={newDeal} dealsToRemove={dealsToRemove} />
    </ModalDrawer>
  );
}
